import { Fragment, Key, useState } from 'react';
import useComment from '../../../hooks/useCommentHook';
import { useCommentsStore } from '../../../stores/commentsStore';
import { Button } from '../../atoms/Button';
import BrainLoadingWrapper from '../../molecules/BrainLoading';
import NamePill from '../../molecules/NamePill';
import { Typography } from '../../molecules/Typography';
import CommentCard from './components/comment/indext';
import InputDiv from './components/inputDiv';
import useDeleteComment from '../../../hooks/useDeleteComment';
import useUpdateComment from '../../../hooks/useUpdateComment';
import MobileCommentCarousel from './components/mobileCarousel';
import CommentsDrawer from './components/commentsDrawer';

export interface ICommentAPI {
  area: string;
  createdAt: Date;
  display: boolean;
  _id: string;
  id: string;
  isAdmin: boolean;
  isFixed: boolean;
  isLiked: boolean;
  level: string;
  likes: any[];
  children: any[];
  qtdLikes: number;
  subarea: string;
  targetId: any;
  targetType: string;
  text: string;
  updatedAt: Date;
  user: any;
}

interface IComment {
  title: string;
  targetId: string;
  parent?: string;
  targetType: 'Lesson' | 'Exercise' | 'Live';
  isModal?: boolean;
  showTitle?: boolean;
  titleSize?: 'h3' | 'h5';
}

export default function Comment({
  title = 'Comentários',
  targetId,
  targetType,
  isModal = false,
  showTitle = true,
  titleSize = 'h3',
}: IComment) {
  const {
    isLoading,
    isLoadingMore,
    addComment,
    likeComment,
    isError,
    fetchMoreComments,
  } = useComment(targetId);
  const { mutate, isPending } = useDeleteComment();
  const { updateComment } = useUpdateComment();

  const {
    comments,
    totalComments,
    enableShowAll,
    addCommentState,
    deleteCommentState,
    upvoteCommentState,
    updateCommentState,
  } = useCommentsStore();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const upvoteComment = async (id: string) => {
    const data = await likeComment(id);
    upvoteCommentState(data);
  };

  const newComment = async (type: string, text: string, id?: string) => {
    if (text.length < 3) {
      return;
    }
    const body = {
      text,
      targetType: targetType,
      targetId: targetId,
      parent: id,
    };
    if (!id) {
      delete body.parent;
    }
    const data = await addComment(body);
    addCommentState(data);
  };

  const handleUpdateComment = async (comment: any, newComment: string) => {
    const commentId = comment.id || comment._id;
    updateComment(
      { id: commentId, data: { text: newComment } },
      {
        onSuccess: () => {
          updateCommentState(commentId, newComment);
        },
      }
    );
  };

  const deleteComment = async (comment: any) => {
    const commentId = comment.id || comment._id;
    mutate(commentId, {
      onSuccess: () => {
        deleteCommentState(comment);
      },
    });
  };

  return (
    <div className='m-auto flex w-full flex-col gap-xxl'>
      <div className='hidden md:block'>
        <div className='flex w-full flex-col gap-xxl'>
          {showTitle && (
            <div className='flex w-full flex-row items-end justify-between'>
              <Typography type='title' size={titleSize}>
                {title}
              </Typography>
              <Typography size='small'>{totalComments} comentários</Typography>
            </div>
          )}
          <InputDiv onSubmit={(text: string) => newComment('comment', text)} />
          {isError && (
            <Typography className='text-center'>
              Não foi possível carregar os comentários
            </Typography>
          )}
          {isLoading && <BrainLoadingWrapper />}
          {!isError && !isLoading && (
            <Fragment>
              <div className='flex h-fit w-full flex-col gap-xl'>
                {comments.map((it: ICommentAPI, i: Key | null | undefined) => (
                  <div className='items-top flex flex-row gap-[24px]' key={i}>
                    <div className='w-12'>
                      <NamePill
                        profile={it.user.picture}
                        name={it.user.name}
                        color={isModal ? 'mono' : 'primary'}
                      />
                    </div>
                    <div className='b flex w-5/6 flex-1 flex-col gap-xl'>
                      <CommentCard
                        comment={it}
                        disableReply={!it._id}
                        onSubmit={(text: string) =>
                          newComment('reply', text, it._id)
                        }
                        onLike={() => upvoteComment(it._id)}
                        key={i}
                        isModal={isModal}
                        onDelete={() => {
                          deleteComment(it);
                        }}
                        deleteLoading={isPending}
                        onUpdate={(value: string) => {
                          handleUpdateComment(it, value);
                        }}
                      />
                      {it.children &&
                        it.children.length > 0 &&
                        it.children.map((reply: ICommentAPI, i: number) => {
                          return (
                            <div
                              className='items-top flex flex-row gap-[24px]'
                              key={i}
                            >
                              <div className='w-12'>
                                <NamePill
                                  profile={reply.user.picture}
                                  name={reply.user.name}
                                  color={isModal ? 'mono' : 'primary'}
                                />
                              </div>
                              <div className='flex flex-1'>
                                <CommentCard
                                  comment={reply}
                                  disableReply={true}
                                  onSubmit={() => {}}
                                  key={i}
                                  onLike={() => {
                                    upvoteComment(reply._id);
                                  }}
                                  isModal={isModal}
                                  onDelete={() => {
                                    deleteComment(reply);
                                  }}
                                  deleteLoading={isPending}
                                  onUpdate={(value: string) => {
                                    handleUpdateComment(reply, value);
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
              <div className='mb-10 flex h-fit w-full justify-end'>
                {enableShowAll && (
                  <Button
                    variant='text'
                    size='regular'
                    LeftIcon='expand_more'
                    className='p-0'
                    loading={isLoadingMore}
                    onClick={() => fetchMoreComments()}
                  >
                    Ver mais comentários
                  </Button>
                )}
              </div>
            </Fragment>
          )}
        </div>
      </div>

      <div className='block md:hidden'>
        {isError && (
          <Typography className='text-center'>
            Não foi possível carregar os comentários
          </Typography>
        )}
        {isLoading && <BrainLoadingWrapper />}
        {!isError && !isLoading && (
          <div className='flex flex-col gap-lg'>
            <MobileCommentCarousel
              comments={comments}
              totalComments={totalComments}
              onLike={upvoteComment}
              onReply={(text: string, id: string) =>
                newComment('reply', text, id)
              }
              onDelete={deleteComment}
              onUpdate={handleUpdateComment}
              deleteLoading={isPending}
              onOpenDrawer={() => setIsDrawerOpen(true)}
            />
            <InputDiv
              onSubmit={(text: string) => newComment('comment', text)}
            />
          </div>
        )}

        <CommentsDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          comments={comments}
          totalComments={totalComments}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          enableShowAll={enableShowAll}
          onLike={upvoteComment}
          onReply={(text: string, id: string) => newComment('reply', text, id)}
          onNewComment={(text: string) => newComment('comment', text)}
          onDelete={deleteComment}
          onUpdate={handleUpdateComment}
          deleteLoading={isPending}
          fetchMoreComments={fetchMoreComments}
        />
      </div>
    </div>
  );
}
